import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Trash2, X } from "lucide-react";
import { inspectionsAPI } from "../../../../../services/api";
import LoadingButton from "./LoadingButton";

const InspectionAssignmentModal = ({
  open,
  onClose,
  projectId,
  inspection,
  teamOptions = [],
  teamLoading,
}) => {
  const inspectionId = inspection?.id;
  const [assignedToUserId, setAssignedToUserId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [error, setError] = useState("");

  const selectedTeamMember = useMemo(
    () =>
      (teamOptions || []).find(
        (x) => Number(x.value) === Number(assignedToUserId),
      ),
    [teamOptions, assignedToUserId],
  );

  const loadAssignments = useCallback(async () => {
    if (!open || !projectId || !inspectionId) return;
    setLoading(true);
    setError("");
    try {
      const res = await inspectionsAPI.listInspectionAssignments(
        projectId,
        inspectionId,
      );
      setAssignments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Failed to load inspection assignments.",
      );
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [open, projectId, inspectionId]);

  useEffect(() => {
    if (!open) return;
    setAssignedToUserId("");
    setDueDate("");
    setNotes("");
    loadAssignments();
  }, [open, loadAssignments]);

  if (!open || !inspection) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!assignedToUserId) {
      setError("Select a project team member to assign.");
      return;
    }
    setAssigning(true);
    setError("");
    try {
      await inspectionsAPI.assignInspection(projectId, inspectionId, {
        assigned_to_user_id: Number(assignedToUserId),
        assignment_role: selectedTeamMember?.role || null,
        due_date: dueDate || null,
        notes: notes.trim() || null,
      });
      setAssignedToUserId("");
      setDueDate("");
      setNotes("");
      await loadAssignments();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to assign inspection.");
    } finally {
      setAssigning(false);
    }
  };

  const remove = async (assignmentId) => {
    setRemovingId(assignmentId);
    setError("");
    try {
      await inspectionsAPI.deleteInspectionAssignment(
        projectId,
        inspectionId,
        assignmentId,
      );
      await loadAssignments();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to remove assignment.");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500">
              Inspection Assignment
            </div>
            <h2 className="mt-1 text-lg font-semibold text-gray-900">
              {`Inspection ID:${inspection.id}`}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {inspection.inspection_type || "Inspection type not set"} •{" "}
              {inspection.inspection_date || "No date"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-2 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form
          onSubmit={submit}
          className="grid grid-cols-1 gap-3 rounded-lg border bg-gray-50 p-3 md:grid-cols-[1fr_160px]"
        >
          <div>
            <label className="text-xs font-semibold text-gray-700">
              Assign to project team member
            </label>
            <select
              value={assignedToUserId}
              onChange={(e) => setAssignedToUserId(e.target.value)}
              disabled={teamLoading || assigning}
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
              required
            >
              <option value="">Select user</option>
              {(teamOptions || []).map((opt) => (
                <option key={opt.key || opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700">
              Due date
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-gray-700">Notes</label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
              placeholder="Optional assignment instructions…"
            />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <LoadingButton
              type="submit"
              loading={assigning}
              loadingText="Assigning…"
              disabled={assigning || !assignedToUserId}
              className="rounded-md bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
            >
              Assign Inspection
            </LoadingButton>
          </div>
        </form>

        <div className="mt-5">
          <div className="mb-2 text-sm font-semibold text-gray-800">
            Current assigned users
          </div>
          {loading && (
            <div className="text-sm text-gray-500">Loading assignments…</div>
          )}
          {!loading && assignments.length === 0 && (
            <div className="rounded-md border border-dashed p-3 text-sm text-gray-500">
              No users assigned to this inspection yet.
            </div>
          )}
          {!loading && assignments.length > 0 && (
            <div className="space-y-2">
              {assignments.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between gap-3 rounded-md border p-3 text-sm"
                >
                  <div>
                    <div className="font-medium text-gray-900">
                      {a.assigned_to_name || `User ${a.assigned_to_user_id}`}
                    </div>
                    <div className="text-xs text-gray-500">
                      {a.assignment_role || "assigned"}
                      {a.due_date ? ` • Due ${a.due_date}` : ""}
                      {a.notes ? ` • ${a.notes}` : ""}
                    </div>
                  </div>
                  <LoadingButton
                    loading={removingId === a.id}
                    loadingText="Removing…"
                    spinnerSize="small"
                    onClick={() => remove(a.id)}
                    className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-xs text-white hover:bg-red-700"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </LoadingButton>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default InspectionAssignmentModal;
